import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getCampanhasDuplicadas } from '@/services/api/connections';

/**
 * Aviso de campanha compartilhada entre páginas.
 *
 * POR QUE ELE EXISTE
 * A receita não é rateada por estimativa: a origem devolve um valor por
 * `utm_campaign` e o cadastro da página diz a quem pertence. Quando DUAS
 * páginas guardam a mesma campanha, a origem continua devolvendo um valor só e
 * não há como saber quem gerou o quê — a tela divide igual entre elas.
 *
 * Sem este aviso o número parece medido e ninguém desconfia. Foi assim que uma
 * página árabe pausada, com zero de tudo, apareceu com a receita de uma página
 * polonesa ativa: as duas dividem `tls-shein-pl-pl-juliajankowska-chatbot`.
 *
 * `UTM_CAMPAIGN` (flow) deveria ser 1:1 — repetida é erro de cadastro.
 * `UTM_CAMPAIGN_BROAD` é compartilhada por desenho e só entra aqui quando
 * junta páginas de idiomas diferentes, que é quando deixa de fazer sentido.
 *
 * Com uma página no filtro mostra só os grupos que a tocam; sem filtro, o
 * panorama. Os dois casos importam: com filtro a pessoa quer saber se AQUELE
 * número é confiável, sem filtro quer saber quanto da tela está afetado.
 */

interface Props {
    /** `page_id` da Meta vindo do filtro global, quando há uma página. */
    paginaId?: string | null;
    /** Nome da página filtrada, só para o texto. */
    nomePagina?: string;
}

export default function AvisoCampanhasDuplicadas({ paginaId, nomePagina }: Props) {
    const [aberto, setAberto] = useState(false);

    const { data } = useQuery({
        queryKey: ['campanhas-duplicadas', paginaId ?? null],
        queryFn: () => getCampanhasDuplicadas(
            paginaId ? { meta_page_id: paginaId } : { apenas_ativas: true },
        ),
        staleTime: 5 * 60 * 1000,
        refetchOnWindowFocus: false,
    });

    if (!data || data.total_grupos === 0) return null;

    const comFiltro = !!paginaId;
    const erros = data.grupos.filter(g => g.severidade === 'erro');

    return (
        <div className="aviso aviso-red">
            <div style={{ width: '100%' }}>
                <strong>
                    {comFiltro
                        ? 'Esta página divide campanha com outras.'
                        : 'Há campanhas compartilhadas entre páginas.'}
                </strong>{' '}
                {comFiltro ? (
                    <>
                        A receita de {nomePagina || 'desta página'} é <strong>dividida</strong> com
                        as páginas abaixo, porque elas guardam o mesmo valor de campanha no
                        cadastro. A origem devolve um número só e não diz quem gerou o quê.
                    </>
                ) : (
                    <>
                        <strong>{data.total_grupos}</strong> campanha(s) aparecem em mais de uma
                        página, afetando <strong>{data.paginas_afetadas}</strong> páginas. A receita
                        delas é dividida em partes iguais, então nenhuma tem número próprio.
                        {erros.length > 0 && (
                            <> {erros.length} são de <strong>flow</strong>, que deveria ser uma
                            campanha por página — essas são erro de cadastro.</>
                        )}
                    </>
                )}
                {' '}
                <button type="button" className="clearall" onClick={() => setAberto(v => !v)}>
                    {aberto ? 'ocultar' : 'ver quais'}
                </button>
                {' · '}
                <a href="/meta/connection" className="clearall">corrigir em Conexões</a>

                {aberto && (
                    <div className="dupgrid">
                        {data.grupos.map(g => (
                            <div key={`${g.chave}-${g.campanha}`} className="dupitem">
                                <div className="dup-h">
                                    <code>{g.campanha}</code>
                                    <span className={`dup-tag dup-${g.severidade}`}>
                                        {g.canal === 'flow' ? 'flow · deveria ser 1:1' : 'broadcast'}
                                    </span>
                                </div>
                                <div className="dup-pgs">
                                    {g.paginas.map(p => (
                                        <span key={p.id} className="dup-pg">
                                            {p.nome}
                                            {p.idioma && <i>{p.idioma}</i>}
                                            {!p.ativa && <b>pausada</b>}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
